import React, { useEffect, useState } from 'react';
import {
  Box,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CalendarToday } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

interface DateRangePickerProps {
  startDate: string | null;
  endDate: string | null;
  onStartDateChange: (date: string | null) => void;
  onEndDateChange: (date: string | null) => void;
  onQuickSelect: (range: string) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onQuickSelect,
}) => {
  // Keep draft selection local: YEAR changes should NOT notify the parent (and thus NOT fire APIs).
  // Only commit to parent after MONTH is selected (onAccept).
  const [startDateValue, setStartDateValue] = useState<Dayjs | null>(startDate ? dayjs(startDate) : null);
  const [endDateValue, setEndDateValue] = useState<Dayjs | null>(endDate ? dayjs(endDate) : null);
  const [quickSelect, setQuickSelect] = React.useState<string>('');

  // If parent updates the range while dialog is open, keep pickers in sync.
  useEffect(() => {
    setStartDateValue(startDate ? dayjs(startDate) : null);
  }, [startDate]);

  useEffect(() => {
    setEndDateValue(endDate ? dayjs(endDate) : null);
  }, [endDate]);

  const commitStartDate = (date: Dayjs | null) => {
    setStartDateValue(date);
    onStartDateChange(date ? date.format('YYYY-MM') : null);
  };

  const commitEndDate = (date: Dayjs | null) => {
    setEndDateValue(date);
    onEndDateChange(date ? date.format('YYYY-MM') : null);
  };

  const handleQuickSelect = (range: string) => {
    const today = dayjs();
    const minDate = dayjs().subtract(5, 'year').startOf('year');
    const getFiscalYearStart = (d: Dayjs) => {
      // Fiscal year assumed: Apr (month=3) -> Mar
      const fyStartYear = d.month() >= 3 ? d.year() : d.year() - 1;
      return d.year(fyStartYear).month(3).startOf('month');
    };
    const getFiscalYearEnd = (fyStart: Dayjs) => fyStart.add(11, 'month').endOf('month');

    let start: Dayjs | null = null;
    let end: Dayjs | null = null;
    setQuickSelect(range);

    switch (range) {
      case '3months':
        start = today.subtract(3, 'month').startOf('month');
        end = today.subtract(1, 'month').endOf('month');
        break;
      case '6months':
        start = today.subtract(6, 'month').startOf('month');
        end = today.subtract(1, 'month').endOf('month');
        break;
      case 'year':
        start = getFiscalYearStart(today);
        end = getFiscalYearEnd(start); // Apr -> Mar (full fiscal year including March)
        break;
      case 'lastyear':
        start = getFiscalYearStart(today).subtract(1, 'year');
        end = getFiscalYearEnd(start);
        break;
      case 'thismonth':
        start = today.startOf('month');
        end = today.endOf('month');
        break;
      case 'lastmonth':
        start = today.subtract(1, 'month').startOf('month');
        end = today.subtract(1, 'month').endOf('month');
        break;
    }

    // Ensure dates are within the allowed range (for fiscal year allow up to FY end = March)
    if (start && end) {
      const defaultMax = dayjs().endOf('month');
      const maxDate = range === 'year' ? getFiscalYearEnd(getFiscalYearStart(today)) : defaultMax;
      start = start.isBefore(minDate) ? minDate : start;
      end = end.isAfter(maxDate) ? maxDate : end;

      setStartDateValue(start);
      setEndDateValue(end);
      onStartDateChange(start.format('YYYY-MM'));
      onEndDateChange(end.format('YYYY-MM'));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <DatePicker
          label="From"
          value={startDateValue}
          // onChange fires on YEAR selection too — keep this local.
          onChange={(date) => setStartDateValue(date)}
          // onAccept fires once the selection is committed (after MONTH selection).
          onAccept={(date) => commitStartDate(date)}
          views={['year', 'month']}
          format="MMM YYYY"
          closeOnSelect
          minDate={dayjs().subtract(5, 'year').startOf('year')}
          maxDate={dayjs().endOf('month')}
          slotProps={{
            textField: {
              size: 'small',
              sx: { minWidth: 120 },
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            },
          }}
        />

        <DatePicker
          label="To"
          value={endDateValue}
          onChange={(date) => setEndDateValue(date)}
          onAccept={(date) => commitEndDate(date)}
          views={['year', 'month']}
          format="MMM YYYY"
          closeOnSelect
          minDate={startDateValue || dayjs().subtract(5, 'year').startOf('year')}
          // maxDate={dayjs().endOf('month')}
          slotProps={{
            textField: {
              size: 'small',
              sx: { minWidth: 120 },
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={quickSelect}
            onChange={(e) => handleQuickSelect(e.target.value)}
            displayEmpty
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="" disabled>
              <em>Quick Select</em>
            </MenuItem>
            <MenuItem value="thismonth">This Month</MenuItem>
            <MenuItem value="lastmonth">Last Month</MenuItem>
            <MenuItem value="3months">Last 3 Months</MenuItem>
            <MenuItem value="6months">Last 6 Months</MenuItem>
            <MenuItem value="year">This Fiscal Year</MenuItem>
            <MenuItem value="lastyear">Last Fiscal Year</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
